import React, { useState, useEffect } from 'react';
import GeneralIcon from '../../../../../assets';
import discountStatsService from '../../../../../services/discountStatsService';
import discountService from '../../../../../services/discountService';

export default function DetalhesDesconto({ discount, onClose, onSave }) {
  const [stats, setStats] = useState(null);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    async function loadStats() {
      try {
        const data = await discountStatsService.getDiscountStats(discount.id);
        setStats(data);
      } catch (err) {
        console.error('Error loading stats:', err);
      }
    }
    loadStats();
  }, [discount.id]);

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  function isExpired() {
    if (!discount.finalDate) return false;
    return new Date(discount.finalDate) < new Date();
  }

  async function handleToggleStatus() {
    try {
      await discountService.toggleDiscountStatus(discount.id);
      onSave && onSave();
      onClose && onClose();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Tem certeza que deseja excluir este desconto?')) return;

    try {
      await discountService.deleteDiscount(discount.id);
      onSave && onSave();
      onClose && onClose();
    } catch (err) {
      console.error('Error deleting discount:', err);
    }
  }

  async function handleReactivate() {
    setDateError('');

    if (!newDate) {
      setDateError('Data é obrigatória para reativar');
      return;
    }

    const selectedDate = new Date(newDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate <= today) {
      setDateError('A data deve ser futura');
      return;
    }

    try {
      await discountService.reactivateDiscount(discount.id, newDate);
      onSave && onSave();
      onClose && onClose();
    } catch (err) {
      console.error('Error reactivating discount:', err);
      setDateError('Erro ao reativar desconto');
    }
  }

  const getItemsDisplay = () => {
    if (!discount.affectedItems || discount.affectedItems.length === 0) {
      return 'Nenhum item selecionado';
    }
    return discount.affectedItems.join(', ');
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 -ml-2 rounded hover:bg-gray-100 transition-colors"
            >
              ←
            </button>
            <h3 className="text-lg font-semibold">Detalhes do desconto</h3>
          </div>
          <button
            onClick={handleDelete}
            className="text-sm text-red-500 hover:text-red-700 transition-colors hover:bg-red-50 px-3 py-2 rounded"
          >
            Excluir
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleStatus}
              disabled={discount.finished}
              className={`flex items-center gap-2 text-sm transition-colors ${
                discount.finished
                  ? 'text-gray-400 cursor-not-allowed'
                  : discount.active
                  ? 'text-green-600 hover:text-green-700'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <GeneralIcon iconName={discount.active ? 'ligar' : 'desligar'} className="w-5 h-5" />
              {discount.active ? 'Desativar desconto' : 'Ativar desconto'}
            </button>
          </div>

          {/* Title */}
          <div>
            <h4 className="text-xs text-gray-500 mb-1">Título</h4>
            <div className="text-base font-semibold text-gray-900">{discount.name}</div>
          </div>

          {/* Description */}
          {discount.description && (
            <div>
              <h4 className="text-xs text-gray-500 mb-1">Descrição</h4>
              <div className="text-sm text-gray-700">{discount.description}</div>
            </div>
          )}

          {/* About */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Sobre</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500">Tipo de desconto</div>
                <div className="text-gray-900">{discount.type}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Data de finalização</div>
                <div className={`${isExpired() ? 'text-red-600' : 'text-gray-900'}`}>
                  {discount.finalDate ? formatDate(discount.finalDate) : '-'}
                </div>
              </div>
            </div>
            {discount.finished && (
              <div className="mt-3 px-3 py-2 bg-teal-50 text-teal-700 text-sm rounded-lg">
                Finalizado
              </div>
            )}
          </div>

          {/* Usage */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Uso</h4>
            <div className="text-sm">
              <div className="text-xs text-gray-500">Valor do desconto</div>
              <div className="text-gray-900 mb-3">{discount.value}%</div>

              {stats && (
                <>
                  <div className="px-3 py-2 bg-purple-50 text-purple-700 text-sm rounded-lg mb-2">
                    Usado {stats.usageCount} vezes neste período
                  </div>
                  <div className="text-xs text-gray-500">
                    Total de {stats.usageCount} utilizações
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Application */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Aplicação</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500">Tipo de alvo</div>
                <div className="text-gray-900">{discount.applyTo || 'Todos os itens'}</div>
              </div>
            </div>

            {discount.affectedItems && discount.affectedItems.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {discount.affectedItems.map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Reactivate button for finished discounts */}
          {discount.finished && (
            <button
              onClick={() => setShowReactivateModal(true)}
              className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Reativar desconto
            </button>
          )}
        </div>
      </div>

      {/* Reactivate Modal */}
      {showReactivateModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Ativar desconto</h3>
            <p className="text-sm text-gray-600 mb-4">
              Para continuar usando este desconto é necessário atualizá-lo.
            </p>

            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">
                Data de finalização*
              </label>
              <input
                type="date"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                  dateError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {dateError && (
                <div className="text-red-500 text-xs mt-1">{dateError}</div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowReactivateModal(false);
                  setNewDate('');
                  setDateError('');
                }}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleReactivate}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
              >
                Atualizar e ativar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
